import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
    APP_INITIALIZER,
    ErrorHandler,
    inject,
    LOCALE_ID,
    NgModule,
    provideAppInitializer
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import * as Sentry from '@sentry/angular';
import {
    I18NEXT_SERVICE,
    I18NextLoadResult,
    I18NextModule,
    ITranslationService,
    defaultInterpolationFormat
} from 'angular-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ChunkLoadErrorHandler, CoreModule } from './core';
import { CustomIconService } from './util/custom-icon.service';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { DowntimeInterceptor } from './core/downtime.interceptor';
// TODO: Export these functions to another file (i18n)

const i18nextOptions = {
    whitelist: ['en', 'de', 'es', 'fr', 'pl', 'ro', 'pt'],
    fallbackLng: 'en',
    debug: false,
    returnEmptyString: false,
    ns: [
        'herds',
        'portfolio',
        'producer-information',
        'herd-ingredients',
        'herd-nutrient',
        'herd-rations',
        'account-password',
        'account-language',
        'account-number-format',
        'account-settings',
        'account-dashboard-herd-display',
        'account-dashboard-display',
        'account-units-of-measure',
        'company',
        'company-invite',
        'company-herd-invite',
        'company-setup',
        'company-connect',
        'modify-company-user',
        'billing',
        'dashboard-edit',
        'herd-milk-pricing',
        'herd-config',
        'herd-subscriptions',
        'create-custom-dashboard',
        'access',
        'herd-access',
        'herd-details',
        'support',
        'drawer-menu',
        'herd-events',
        'print-preview',
        'connected-herd-company-access',
        'herd-disposal-codes',
        'alerts',
        'alert-page',
        'pay-statements',
        'equity-statements',
        'quality-bonus-statements',
        'login',
        'reset',
        'reset-finish',
        'error-pages',
        'not-found',
        'custom-cookies-snackbar',
        'select-dashboard',
        'register',
        'invitation-declined',
        'herd-locations',
        'herd-data',
        'herd',
        'create',
        'create-herd',
        'create-company',
        'connected-herd',
        'connected-herd-companies',
        'maintenance',
        'details',
        'checkbox-nested-group',
        'dashboard-host',
        'card-sheet',
        'compare-mode',
        'dashboard',
        'manage-dashboard',
        'shell',
        'calculate-summary-facts',
        'dashboard-card-handler',
        'date-context',
        'listen-for-updates',
        'mdd-highcharts',
        'sheet-data-table',
        'theme-service',
        'dropdown-component',
        'series',
        'series-portfolio',
        'tooltip-messages',
        'cards',
        'cards-portfolio',
        'default-print-profiles',
        'categories',
        'feature-set',
        'filter-dropdown',
        'mat-paginator-labels',
        'user-types',
        'integrations',
        'global',
        'mdd-table',
        'dashboard-export-page',
        'smp-report-forms',
        'glanbia-documents',
        'glanbia-labels',
        'mdd-card-labels',
        'ampi-calculator',
        'load-details-notification-page',
        'feed-king-subjects',
        'feed-king-decrees',
        'feed-king-feeder-info',
        'data-status-card',
        'hilmar-labels',
        'weighback-pen-limits',
        'feed-king-shifts',
        'notifications',
        'account-snark-messages',
        'snark-opt-in'
    ],
    interpolation: {
        format: I18NextModule.interpolationFormat(defaultInterpolationFormat)
    },
    backend: {
        loadPath: 'assets/locales/{{lng}}/{{ns}}.json'
    },
    // lang detection plugin options
    detection: {
        // order and from where user language should be detected
        order: ['querystring', 'cookie'],

        // keys or params to lookup language from
        lookupCookie: 'lang',
        lookupQuerystring: 'lng',

        // cache user language on
        caches: ['cookie'],

        // optional expire and domain for set cookie
        cookieMinutes: 10080 // 7 days
    }
};

export function appInit(i18next: ITranslationService) {
    return () => {
        const promise: Promise<I18NextLoadResult> = i18next
            .use(HttpApi)
            .use(LanguageDetector)
            .init(i18nextOptions);

        return promise;
    };
}

export function localeIdFactory(i18next: ITranslationService) {
    if (i18next.language !== 'en') {
        return 'en-US';
    } else {
        return i18next.language;
    }
}

export const I18N_PROVIDERS = [
    {
        provide: APP_INITIALIZER,
        useFactory: appInit,
        deps: [I18NEXT_SERVICE],
        multi: true
    },
    {
        provide: LOCALE_ID,
        deps: [I18NEXT_SERVICE],
        useFactory: localeIdFactory
    }
];

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        AppRoutingModule,
        BrowserModule,
        BrowserAnimationsModule,
        CoreModule,
        I18NextModule.forRoot(),
        MatSnackBarModule,
        RouterModule
    ],
    providers: [
        I18N_PROVIDERS,
        CustomIconService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DowntimeInterceptor,
            multi: true
        },
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        {
            provide: ErrorHandler,
            useClass: ChunkLoadErrorHandler
        },
        {
            provide: Sentry.TraceService,
            deps: [Router]
        },
        provideAppInitializer(() => {
            inject(Sentry.TraceService);
        })
    ]
})
export class AppModule {}
